import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { useRef, useEffect, useState } from 'react';
import {
  fetchSectionList,
  fetchSectionAssets,
} from '@/features/sections/sectionsSlice';
import MediaTile from '@/components/content/MediaTile/MediaTile';
import PodcastTile from '@/components/content/PodcastTile/PodcastTile';
import Toast from '@/components/ui/Toast/Toast';
import {
  sectionAssetToMediaItem,
  sectionAssetToPodcastItem,
} from '@/services/tabFeedService';
import type { MediaItem, PodcastItem, Section, SectionAssetCard } from '@/types';
import styles from './SectionViewAllPage.module.css';

const PAGE_LIMIT = 24;

/** Derive ContentRow type from section slug so we pick the right tile component */
function rowTypeFromSection(section: Section | undefined): 'default' | 'trending' | 'pod' {
  if (!section) return 'default';
  if (section.slug === 'podcast') return 'pod';
  return 'default';
}

export default function SectionViewAllPage() {
  const { sectionId } = useParams<{ sectionId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Section metadata from the library or flat list
  const section = useAppSelector((s) =>
    [...s.sections.library, ...s.sections.sections].find((sec) => sec.id === sectionId)
  );

  // Paginated assets from the v3 API
  const bucket = useAppSelector((s) =>
    sectionId ? s.sections.sectionAssets[sectionId] : undefined
  );
  const isLoading = useAppSelector((s) =>
    sectionId ? (s.sections.sectionAssetsLoading[sectionId] ?? false) : false
  );

  // Fetch section list (for metadata) and first page of assets on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!sectionId) return;
    dispatch(fetchSectionList());
    dispatch(fetchSectionAssets({ id: sectionId, limit: PAGE_LIMIT, offset: 0 }));
  }, [dispatch, sectionId]);

  // Track grid column count so we can set correct transform-origin per item
  const gridRef = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(5);

  useEffect(() => {
    if (!gridRef.current) return;
    const measure = () => {
      const el = gridRef.current;
      if (!el) return;
      const colCount = window.getComputedStyle(el).gridTemplateColumns.split(' ').length;
      setCols(colCount);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(gridRef.current);
    return () => ro.disconnect();
  }, []);

  const title = section?.label ?? 'Collection';
  const totalCount = bucket?.total ?? 0;
  const assets: SectionAssetCard[] = bucket?.items ?? [];
  const rowType = rowTypeFromSection(section);
  const canLoadMore = !isLoading && assets.length > 0 && assets.length < totalCount;

  const handleLoadMore = () => {
    if (!sectionId) return;
    dispatch(fetchSectionAssets({ id: sectionId, limit: PAGE_LIMIT, offset: assets.length }));
  };

  // Convert SectionAssetCard → tile item shapes
  const mediaItems = assets.map((a, i) => sectionAssetToMediaItem(a, i)) as MediaItem[];
  const podItems = assets.map((a, i) => sectionAssetToPodcastItem(a, i)) as PodcastItem[];

  return (
    <div className={styles.page}>

      {/* ─── Header ─────────────────────────────────────────────────────── */}
      <div className={styles.header}>
        <button
          className={styles.backBtn}
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>

        <div className={styles.headerText}>
          <h1 className={styles.sectionTitle}>{title}</h1>
          {section && (
            <p className={styles.sectionSubtitle}>
              {totalCount > 0 ? `${totalCount} item${totalCount !== 1 ? 's' : ''}` : 'Browse your collection'}
            </p>
          )}
        </div>

        <div className={styles.headerActions}>
          <span className={styles.itemCount}>
            {!isLoading && assets.length > 0 ? `${assets.length} of ${totalCount}` : ''}
          </span>
        </div>
      </div>

      {/* ─── Grid ───────────────────────────────────────────────────────── */}
      <div className={styles.gridWrapper}>
        <div className={styles.grid} ref={gridRef}>
          {isLoading && assets.length === 0
            ? Array.from({ length: PAGE_LIMIT }, (_, i) => (
                <div key={i} className={styles.gridItem}>
                  <div className={styles.skeletonCard} />
                </div>
              ))
            : rowType === 'pod'
            ? podItems.map((item, idx) => (
                <div
                  key={item.id}
                  className={styles.gridItem}
                  data-origin={idx % cols === 0 ? 'left' : idx % cols === cols - 1 ? 'right' : undefined}
                >
                  <PodcastTile item={item} variant="grid" />
                </div>
              ))
            : mediaItems.map((item, idx) => (
                <div
                  key={item.id}
                  className={styles.gridItem}
                  data-origin={idx % cols === 0 ? 'left' : idx % cols === cols - 1 ? 'right' : undefined}
                >
                  <MediaTile item={item} variant="grid" onPlay={() => navigate(-1)} />
                </div>
              ))
          }
        </div>
      </div>

      {/* ─── Load More ──────────────────────────────────────────────────── */}
      {canLoadMore && (
        <div className={styles.loadMoreRow}>
          <button className={styles.loadMoreBtn} onClick={handleLoadMore}>
            Load more · {totalCount - assets.length} remaining
          </button>
        </div>
      )}
      {isLoading && assets.length > 0 && (
        <div className={styles.loadMoreRow}>
          <button className={styles.loadMoreBtn} disabled>Loading…</button>
        </div>
      )}

      <Toast />
    </div>
  );
}
